// Acionar guincho (pedido 2026-10-16): guinchos recomendados mais perto do
// Ponto B (destino) — ou qualquer outro —, mensagem com as variáveis de
// Configurar Assistência, envio automático pelo WhatsApp da empresa, link
// do cliente acompanhar e conversa com o guincheiro (respostas lidas do
// WhatsApp a cada poucos segundos).
import { useEffect, useMemo, useRef, useState } from "react";
import { APIProvider, AdvancedMarker, Map as GoogleMap, useMap as useGoogleMap } from "@vis.gl/react-google-maps";
import {
  Ban,
  Check,
  Copy,
  ExternalLink,
  Loader2,
  MapPin,
  MessageCircle,
  Navigation,
  Phone,
  Search,
  Send,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import { GOOGLE_MAPS_CLEAN_STYLE, hasCustomGoogleMapId, resolveGoogleMapId } from "@/lib/google-maps-style";
import { useVehicles } from "@/hooks/queries";
import { useAssistanceVehiclePlans, useDispatchChat, useDispatches, useTowTrucks } from "@/hooks/queries/assistance-center";
import {
  gpsDriveAssistanceCenterService as api,
  TOW_PHASE_LABEL,
  type AssistanceDispatch,
  type TowTruck,
} from "@/services/gpsdrive-assistance-center.service";

type LatLng = { lat: number; lng: number };
const errorText = (e: unknown, fallback: string) => (e instanceof GpsDriveApiError ? e.message : fallback);
const kmBetween = (a: LatLng, b: LatLng) => {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};
const fmtKm = (km: number) => (km < 10 ? `${km.toFixed(1).replace(".", ",")} km` : `${Math.round(km)} km`);
const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
const PHASE_COLOR: Record<string, string> = {
  aguardando: "var(--asst-neutral)",
  a_caminho: "var(--asst-c1)",
  no_local: "var(--asst-open)",
  levando: "var(--asst-c4)",
  concluido: "var(--asst-done)",
  cancelado: "var(--asst-neutral)",
};
const RECOMMENDED = 6;

async function copy(text: string, what: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copiado`);
  } catch {
    window.prompt(`Copie o ${what.toLowerCase()}:`, text);
  }
}

export function AssistanceDispatchPanel({ requestId, onClose }: { requestId: string; onClose: () => void }) {
  const { data, isLoading, error } = useDispatches(requestId);
  const { data: trucks = [] } = useTowTrucks();
  const { data: vehicles } = useVehicles();
  const { data: vehiclePlans } = useAssistanceVehiclePlans();
  const qc = useQueryClient();
  const req = data?.request;
  const dispatches = data?.dispatches ?? [];

  const [selected, setSelected] = useState<string[]>([]);
  const [q, setQ] = useState("");
  const [sendWhatsapp, setSendWhatsapp] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [edited, setEdited] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [links, setLinks] = useState<{ trackUrl: string | null; pointAUrl: string | null }>({ trackUrl: null, pointAUrl: null });
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [chatWith, setChatWith] = useState<AssistanceDispatch | null>(null);
  const [notifying, setNotifying] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  const vehicle = req ? vehicles?.find((v) => Number(v.id) === req.traccarDeviceId) : undefined;
  const pointA: LatLng | null =
    vehicle && (vehicle.lat || vehicle.lng) ? { lat: vehicle.lat, lng: vehicle.lng } : req?.latitude != null && req.longitude != null ? { lat: req.latitude, lng: req.longitude } : null;
  const pointB: LatLng | null = req?.destinationLat != null && req.destinationLng != null ? { lat: req.destinationLat, lng: req.destinationLng } : null;
  const planHit = req ? vehiclePlans?.[String(req.traccarDeviceId)] : undefined;
  const plan = planHit ? (planHit.groupName ? `${planHit.planName} · ${planHit.groupName}` : planHit.planName) : null;

  // Recomendados: mais perto do Ponto B (sem B, do Ponto A).
  const ref = pointB ?? pointA;
  const ranked = useMemo(() => {
    return trucks
      .filter((t) => t.active)
      .map((t) => {
        const pos = t.latitude != null && t.longitude != null ? { lat: t.latitude, lng: t.longitude } : null;
        return {
          truck: t,
          pos,
          toB: pos && pointB ? kmBetween(pos, pointB) : null,
          toA: pos && pointA ? kmBetween(pos, pointA) : null,
          ref: pos && ref ? kmBetween(pos, ref) : null,
        };
      })
      .sort((a, b) => (a.ref ?? 1e9) - (b.ref ?? 1e9));
  }, [trucks, pointA, pointB, ref]);
  const alreadySent = new Set(dispatches.filter((d) => d.phase !== "cancelado").map((d) => d.towTruckId));
  const recommended = ranked.filter((r) => r.ref != null && !alreadySent.has(r.truck.id)).slice(0, RECOMMENDED);
  const others = useMemo(() => {
    const term = q.trim().toLowerCase();
    const rec = new Set(recommended.map((r) => r.truck.id));
    return ranked
      .filter((r) => !rec.has(r.truck.id) && !alreadySent.has(r.truck.id))
      .filter((r) => !term || [r.truck.name, r.truck.city, r.truck.phone].some((v) => v?.toLowerCase().includes(term)))
      .slice(0, 30);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ranked, q, recommended.length, dispatches.length]);

  // Prévia da mensagem (do 1º guincho marcado) — editável antes de enviar.
  const firstSelected = selected[0];
  useEffect(() => {
    let alive = true;
    setPreviewLoading(true);
    api
      .dispatchPreview(requestId, firstSelected)
      .then((p) => {
        if (!alive) return;
        if (!edited) setMessage(p.text);
        setLinks({ trackUrl: p.trackUrl, pointAUrl: p.pointAUrl });
      })
      .catch(() => undefined)
      .finally(() => alive && setPreviewLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId, firstSelected]);

  const toggle = (id: string) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const send = async () => {
    setSendError(null);
    setSending(true);
    try {
      // Mensagem editada vale pra todos; sem edição cada guincho recebe a
      // sua (com o nome e o link dele).
      const res = await api.dispatch(requestId, { towTruckIds: selected, send: sendWhatsapp, messageOverride: edited ? message : null });
      const failed = res.filter((d) => d.sendStatus === "falhou");
      if (failed.length) setSendError(`Não foi possível enviar para: ${failed.map((d) => `${d.towTruckName} (${d.sendError ?? "erro"})`).join(", ")}`);
      setSelected([]);
      setEdited(false);
      setShowPicker(false);
      await qc.invalidateQueries({ queryKey: ["assistance-center"] });
    } catch (e) {
      setSendError(errorText(e, "Não foi possível enviar"));
    } finally {
      setSending(false);
    }
  };

  const notifyClient = async () => {
    setNotifying(true);
    try {
      await api.notifyClient(requestId, null);
      toast.success("Link enviado ao cliente pelo WhatsApp");
    } catch (e) {
      toast.error(errorText(e, "Não foi possível enviar ao cliente"));
    } finally {
      setNotifying(false);
    }
  };

  const activeDispatch = dispatches.find((d) => d.startedAt && d.phase !== "cancelado");
  const picking = dispatches.length === 0 || showPicker;
  const trackUrl = links.trackUrl ?? (req?.trackToken ? `${window.location.origin}/acompanhar/${req.trackToken}` : null);
  const pointAUrl = links.pointAUrl ?? (req?.vehicleShareToken ? `${window.location.origin}/ao-vivo/${req.vehicleShareToken}` : null);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      {/* Celular: tela cheia (Registrar Assistência do app), com o "Fechar"
          abaixo da barra de status; computador: janela grande. */}
      <DialogContent
        className="asst-root h-[100dvh] max-h-[100dvh] max-w-6xl content-start overflow-y-auto rounded-none p-4 sm:h-auto sm:max-h-[96vh] sm:rounded-lg sm:p-6 [&>button:last-child]:hidden"
        style={{ paddingTop: "max(1rem, env(safe-area-inset-top, 0px))" }}
      >
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <DialogTitle>Acionar guincho</DialogTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Fechar
          </Button>
        </DialogHeader>
        {error && !data ? (
          <p className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            {errorText(error, "Não foi possível abrir o acionamento.")} Se for falta de permissão, peça ao administrador a permissão “Assistência (guinchos)”.
          </p>
        ) : isLoading || !req ? (
          <div className="flex h-60 items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Carregando…
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-[1fr_420px]">
            {/* Coluna principal */}
            <div className="min-w-0 space-y-4">
              <div className="rounded-xl border bg-muted/30 p-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{req.serviceTypeName}</span>
                  {plan ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[color-mix(in_srgb,var(--asst-done)_15%,transparent)] px-2 py-0.5 text-[11px] font-bold text-[var(--asst-done)]">
                      <ShieldCheck className="h-3 w-3" /> Assistência {plan}
                    </span>
                  ) : (
                    <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:text-amber-300">Veículo sem assistência na lista</span>
                  )}
                </div>
                <div className="mt-1 text-muted-foreground">
                  {req.vehicleName} · {req.clientName}
                  {req.clientPhone && ` · ${req.clientPhone}`}
                </div>
                <div className="mt-2 grid gap-1 text-xs sm:grid-cols-2">
                  <div className="flex gap-1.5">
                    <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[var(--asst-c2)] text-[10px] font-bold text-white">A</span>
                    <span>{req.address ?? (pointA ? "Posição atual do rastreador" : "Sem posição")}</span>
                  </div>
                  <div className="flex gap-1.5">
                    <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[var(--asst-c4)] text-[10px] font-bold text-white">B</span>
                    <span>{req.destinationAddress ?? "Sem destino informado"}</span>
                  </div>
                </div>
                {req.reason && <div className="mt-1 text-xs">“{req.reason}”</div>}
              </div>

              {/* Guinchos acionados */}
              {dispatches.length > 0 && (
                <section className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold">Guinchos acionados</h3>
                    {!showPicker && (
                      <Button variant="outline" size="sm" onClick={() => setShowPicker(true)}>
                        Enviar para mais guinchos
                      </Button>
                    )}
                  </div>
                  <ul className="divide-y rounded-xl border">
                    {dispatches.map((d) => (
                      <DispatchRow key={d.id} d={d} onChat={() => setChatWith(d)} />
                    ))}
                  </ul>
                </section>
              )}

              {/* Escolher guinchos */}
              {picking && (
                <section className="space-y-3">
                  <h3 className="text-sm font-semibold">
                    Recomendados <span className="font-normal text-muted-foreground">— mais perto do {pointB ? "Ponto B (destino)" : "veículo"}</span>
                  </h3>
                  {recommended.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Nenhum guincho com localização. Escolha abaixo.</p>
                  ) : (
                    <div className="grid gap-2 sm:grid-cols-2">
                      {recommended.map((r, i) => (
                        <TruckOption key={r.truck.id} r={r} rank={i + 1} checked={selected.includes(r.truck.id)} onToggle={() => toggle(r.truck.id)} />
                      ))}
                    </div>
                  )}
                  <details className="rounded-xl border p-3" open={!!q}>
                    <summary className="cursor-pointer text-sm font-semibold">Escolher outro guincho</summary>
                    <div className="relative mt-2">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome, cidade ou telefone…" className="h-9 w-full rounded-lg border bg-background pl-9 pr-3 text-sm" />
                    </div>
                    <div className="mt-2 grid max-h-64 gap-2 overflow-y-auto sm:grid-cols-2">
                      {others.map((r) => (
                        <TruckOption key={r.truck.id} r={r} checked={selected.includes(r.truck.id)} onToggle={() => toggle(r.truck.id)} />
                      ))}
                    </div>
                  </details>

                  <div className="space-y-2 rounded-xl border p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold">Mensagem para o guincheiro</span>
                      {edited && (
                        <button
                          type="button"
                          className="text-xs text-primary hover:underline"
                          onClick={() => {
                            setEdited(false);
                            void api.dispatchPreview(requestId, firstSelected).then((p) => setMessage(p.text));
                          }}
                        >
                          Voltar ao texto padrão
                        </button>
                      )}
                    </div>
                    {previewLoading && message == null ? (
                      <div className="h-40 animate-pulse rounded-lg bg-muted" />
                    ) : (
                      <textarea
                        value={message ?? ""}
                        onChange={(e) => {
                          setMessage(e.target.value);
                          setEdited(true);
                        }}
                        rows={10}
                        className="w-full rounded-lg border bg-background px-3 py-2 font-mono text-xs leading-relaxed"
                      />
                    )}
                    <p className="text-[11px] text-muted-foreground">
                      Cada guincho recebe o próprio link da tela de trajeto. O texto padrão fica em Administração → Configurar Assistência.
                      {edited && " Editado: o mesmo texto vai para todos os marcados."}
                    </p>
                    <label className="flex cursor-pointer items-center gap-2 text-sm">
                      <input type="checkbox" checked={sendWhatsapp} onChange={(e) => setSendWhatsapp(e.target.checked)} className="h-4 w-4 accent-[var(--asst-done)]" />
                      Enviar a mensagem automática pelo WhatsApp da empresa
                    </label>
                  </div>

                  {sendError && <p className="text-sm text-destructive">{sendError}</p>}
                  <div className="flex flex-wrap justify-end gap-2">
                    {showPicker && (
                      <Button variant="outline" onClick={() => setShowPicker(false)}>
                        Cancelar
                      </Button>
                    )}
                    <Button onClick={send} disabled={sending || selected.length === 0} className="bg-[var(--asst-done)] text-white hover:opacity-90">
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      {sendWhatsapp ? `Enviar para ${selected.length || ""} guincho${selected.length === 1 ? "" : "s"}` : "Gerar links (sem enviar)"}
                    </Button>
                  </div>
                </section>
              )}
            </div>

            {/* Coluna lateral: mapa + links */}
            <div className="space-y-3">
              <DispatchMap pointA={pointA} pointB={pointB} trucks={ranked.filter((r) => selected.includes(r.truck.id) || r.truck.id === activeDispatch?.towTruckId)} active={activeDispatch} />
              <div className="space-y-2 rounded-xl border p-3 text-sm">
                <div className="text-xs font-semibold">Link para o cliente acompanhar o guincho</div>
                {trackUrl ? (
                  <>
                    <div className="truncate rounded-lg bg-muted px-2 py-1.5 font-mono text-[11px]">{trackUrl}</div>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => copy(trackUrl, "Link")}>
                        <Copy className="h-3.5 w-3.5" /> Copiar
                      </Button>
                      <Button size="sm" variant="outline" asChild>
                        <a href={trackUrl} target="_blank" rel="noreferrer">
                          <ExternalLink className="h-3.5 w-3.5" /> Abrir
                        </a>
                      </Button>
                      <Button size="sm" onClick={notifyClient} disabled={notifying || !req.clientPhone} title={req.clientPhone ? undefined : "Cliente sem celular no cadastro"}>
                        {notifying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageCircle className="h-3.5 w-3.5" />}
                        Enviar ao cliente
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground">Gerado ao acionar o primeiro guincho.</p>
                )}
                {pointAUrl && (
                  <button type="button" onClick={() => copy(pointAUrl, "Link do Ponto A")} className="flex items-center gap-1 text-xs text-primary hover:underline">
                    <MapPin className="h-3 w-3" /> Copiar link do Ponto A (veículo ao vivo)
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        {chatWith && <ChatDialog dispatch={chatWith} onClose={() => setChatWith(null)} />}
      </DialogContent>
    </Dialog>
  );
}

function TruckOption({
  r,
  rank,
  checked,
  onToggle,
}: {
  r: { truck: TowTruck; toA: number | null; toB: number | null };
  rank?: number;
  checked: boolean;
  onToggle: () => void;
}) {
  const t = r.truck;
  const inRange = r.toA != null && r.toA <= t.coverageRadiusKm;
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn("flex items-start gap-2 rounded-xl border p-2.5 text-left transition-colors", checked ? "border-[var(--asst-done)] bg-[color-mix(in_srgb,var(--asst-done)_8%,transparent)] ring-1 ring-[var(--asst-done)]" : "hover:bg-muted/50")}
    >
      <span className={cn("mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded border", checked && "border-[var(--asst-done)] bg-[var(--asst-done)] text-white")}>
        {checked && <Check className="h-3.5 w-3.5" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          {rank && <span className="rounded bg-muted px-1 text-[10px] font-bold">{rank}º</span>}
          <span className="truncate text-sm font-semibold">{t.name}</span>
        </span>
        <span className="block truncate text-xs text-muted-foreground">
          {t.city}/{t.state}
          {t.available24h && " · 24h"}
          {t.phone && ` · ${t.phone}`}
        </span>
        <span className="mt-0.5 flex flex-wrap gap-x-2 text-[11px]">
          {r.toB != null && <span className="font-semibold text-[var(--asst-c4)]">B a {fmtKm(r.toB)}</span>}
          {r.toA != null && <span className={inRange ? "text-[var(--asst-done)]" : "text-amber-700 dark:text-amber-300"}>veículo a {fmtKm(r.toA)}</span>}
          {r.toA == null && r.toB == null && <span className="text-muted-foreground">sem localização</span>}
        </span>
      </span>
    </button>
  );
}

function DispatchRow({ d, onChat }: { d: AssistanceDispatch; onChat: () => void }) {
  const qc = useQueryClient();
  const driverUrl = `${window.location.origin}${d.driverPath}`;
  const cancel = async () => {
    if (!window.confirm(`Cancelar o envio para ${d.towTruckName}? O link dele para de funcionar.`)) return;
    try {
      await api.cancelDispatch(d.id);
      await qc.invalidateQueries({ queryKey: ["assistance-center", "dispatches"] });
    } catch (e) {
      toast.error(errorText(e, "Não foi possível cancelar"));
    }
  };
  const ago = d.lastPosition?.at ? Math.round((Date.now() - new Date(d.lastPosition.at).getTime()) / 1000) : null;
  return (
    <li className="flex flex-wrap items-center gap-2 p-3 text-sm">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold">{d.towTruckName}</span>
          <span
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold"
            style={{ background: `color-mix(in srgb, ${PHASE_COLOR[d.phase]} 15%, transparent)`, color: PHASE_COLOR[d.phase] }}
          >
            {TOW_PHASE_LABEL[d.phase]}
          </span>
        </div>
        <div className="text-xs text-muted-foreground">
          {d.sendStatus === "enviado" && `WhatsApp enviado ${d.sentAt ? fmtTime(d.sentAt) : ""}`}
          {d.sendStatus === "falhou" && <span className="text-destructive">Falhou: {d.sendError}</span>}
          {d.sendStatus === "nao_enviado" && "Link gerado (sem WhatsApp)"}
          {d.openedAt && " · abriu o link"}
          {d.phase === "a_caminho" && ago != null && ` · GPS há ${ago < 60 ? `${ago}s` : `${Math.round(ago / 60)} min`}`}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button size="sm" variant="outline" onClick={onChat} className="relative">
          <MessageCircle className="h-3.5 w-3.5 text-[var(--asst-done)]" /> WhatsApp
          {d.unread && <span className="absolute -right-1 -top-1 h-3 w-3 animate-pulse rounded-full border-2 border-background bg-destructive" title="Respondeu" />}
        </Button>
        <button type="button" onClick={() => copy(driverUrl, "Link do guincheiro")} title="Copiar link do guincheiro" className="grid h-8 w-8 place-items-center rounded-lg hover:bg-muted">
          <Copy className="h-4 w-4" />
        </button>
        {d.phone && (
          <a href={`tel:${d.phone.replace(/\D/g, "")}`} title="Ligar" className="grid h-8 w-8 place-items-center rounded-lg hover:bg-muted">
            <Phone className="h-4 w-4" />
          </a>
        )}
        {!d.finishedAt && d.phase !== "cancelado" && (
          <button type="button" onClick={cancel} title="Cancelar este envio" className="grid h-8 w-8 place-items-center rounded-lg text-destructive hover:bg-destructive/10">
            <Ban className="h-4 w-4" />
          </button>
        )}
      </div>
    </li>
  );
}

function FitAll({ points }: { points: LatLng[] }) {
  const map = useGoogleMap();
  const sig = points.map((p) => `${p.lat.toFixed(3)},${p.lng.toFixed(3)}`).join("|");
  useEffect(() => {
    if (!map || points.length === 0) return;
    if (points.length === 1) {
      map.setCenter(points[0]!);
      map.setZoom(13);
      return;
    }
    const b = new google.maps.LatLngBounds();
    points.forEach((p) => b.extend(p));
    map.fitBounds(b, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, sig]);
  return null;
}

function DispatchMap({
  pointA,
  pointB,
  trucks,
  active,
}: {
  pointA: LatLng | null;
  pointB: LatLng | null;
  trucks: { truck: TowTruck; pos: LatLng | null }[];
  active: AssistanceDispatch | undefined;
}) {
  const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined) ?? "";
  if (!apiKey) return null;
  const live = active?.lastPosition ? { lat: active.lastPosition.lat, lng: active.lastPosition.lng } : null;
  const pts = [pointA, pointB, live, ...trucks.map((t) => t.pos)].filter(Boolean) as LatLng[];
  return (
    <div className="h-72 overflow-hidden rounded-xl border lg:h-80">
      <APIProvider apiKey={apiKey}>
        <GoogleMap
          mapId={resolveGoogleMapId()}
          styles={hasCustomGoogleMapId() ? undefined : GOOGLE_MAPS_CLEAN_STYLE}
          defaultCenter={pts[0] ?? { lat: -30.03, lng: -51.23 }}
          defaultZoom={11}
          disableDefaultUI
          zoomControl
          clickableIcons={false}
          gestureHandling="greedy"
          style={{ height: "100%", width: "100%" }}
        >
          <FitAll points={pts} />
          {pointA && (
            <AdvancedMarker position={pointA} title="Ponto A — veículo" zIndex={20}>
              <div className="grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full border-2 border-white bg-[var(--asst-c2)] text-sm font-bold text-white shadow-lg">A</div>
            </AdvancedMarker>
          )}
          {pointB && (
            <AdvancedMarker position={pointB} title="Ponto B — destino" zIndex={20}>
              <div className="grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full border-2 border-white bg-[var(--asst-c4)] text-sm font-bold text-white shadow-lg">B</div>
            </AdvancedMarker>
          )}
          {trucks.map(
            (t) =>
              t.pos &&
              t.truck.id !== active?.towTruckId && (
                <AdvancedMarker key={t.truck.id} position={t.pos} title={t.truck.name} zIndex={10}>
                  <div className="grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full border-2 border-white bg-slate-600 text-base shadow">🛻</div>
                </AdvancedMarker>
              ),
          )}
          {live && (
            <AdvancedMarker position={live} title={`${active?.towTruckName} — ao vivo`} zIndex={30}>
              <div className="relative -translate-y-1/2">
                <span className="absolute inset-0 animate-ping rounded-full bg-[var(--asst-c1)] opacity-40" />
                <div className="relative grid h-10 w-10 place-items-center rounded-full border-2 border-white bg-[var(--asst-c1)] text-lg shadow-lg">🛻</div>
              </div>
            </AdvancedMarker>
          )}
        </GoogleMap>
      </APIProvider>
    </div>
  );
}

// Conversa com o guincheiro pelo WhatsApp da empresa (atualiza a cada 5 s).
function ChatDialog({ dispatch, onClose }: { dispatch: AssistanceDispatch; onClose: () => void }) {
  const { data, isLoading, error, refetch } = useDispatchChat(dispatch.id);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const count = data?.messages.length ?? 0;
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [count]);
  useEffect(() => {
    void qc.invalidateQueries({ queryKey: ["assistance-center", "dispatches"] });
  }, [count, qc]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    setSendError(null);
    try {
      await api.sendChat(dispatch.id, t);
      setText("");
      await refetch();
    } catch (e) {
      setSendError(errorText(e, "Não foi possível enviar"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="flex h-[100dvh] max-h-[100dvh] max-w-md flex-col gap-0 rounded-none p-0 sm:h-[85vh] sm:rounded-lg [&>button:last-child]:hidden"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)", paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <DialogHeader className="flex-row items-center gap-2 space-y-0 border-b px-4 py-3">
          <DialogTitle className="flex min-w-0 flex-1 items-center gap-2 text-base">
            <MessageCircle className="h-5 w-5 shrink-0 text-emerald-600" />
            <span className="truncate">{dispatch.towTruckName}</span>
            {data?.phone && <span className="shrink-0 text-xs font-normal text-muted-foreground">{data.phone}</span>}
          </DialogTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Fechar
          </Button>
        </DialogHeader>
        <div className="flex-1 space-y-2 overflow-y-auto bg-[#efeae2] p-3 dark:bg-slate-900">
          {isLoading ? (
            <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Carregando conversa…
            </div>
          ) : error ? (
            <p className="p-4 text-center text-sm text-destructive">{errorText(error, "Não foi possível ler a conversa")}</p>
          ) : count === 0 ? (
            <p className="p-4 text-center text-sm text-muted-foreground">Nenhuma mensagem ainda.</p>
          ) : (
            data!.messages.map((m) => (
              <div key={m.id} className={cn("flex", m.fromMe ? "justify-end" : "justify-start")}>
                <div className={cn("max-w-[85%] whitespace-pre-wrap break-words rounded-lg px-3 py-1.5 text-sm shadow-sm", m.fromMe ? "bg-[#d9fdd3] text-slate-900" : "bg-white text-slate-900")}>
                  {m.text}
                  <div className="mt-0.5 text-right text-[10px] text-slate-500">{fmtTime(m.at)}</div>
                </div>
              </div>
            ))
          )}
          <div ref={endRef} />
        </div>
        <div className="border-t p-2">
          {sendError && <p className="px-1 pb-1 text-xs text-destructive">{sendError}</p>}
          <div className="flex items-end gap-2">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
              rows={1}
              placeholder="Mensagem…"
              className="max-h-32 min-h-10 flex-1 resize-none rounded-lg border bg-background px-3 py-2 text-sm"
            />
            <Button onClick={send} disabled={sending || !text.trim()} className="h-10 bg-emerald-600 text-white hover:bg-emerald-700">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
          <p className="px-1 pt-1 text-[10px] text-muted-foreground">
            <Navigation className="mr-0.5 inline h-2.5 w-2.5" />
            Enviado pelo WhatsApp da empresa. Áudios e fotos aparecem só como aviso.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
